import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { SEO } from "@/components/SEO"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog"
import { ArrowDown, Settings, Wallet, Loader2, AlertCircle } from "lucide-react"
import { ToggleableAmount } from "@/components/ui/toggleable-amount"
import bnbLogo from "@/assets/tokens/bnb.svg"
import welephantLogo from "@/assets/tokens/welephant.svg"
import { useEthersContext } from "@/providers/EthersProvider"
import { useTransactionToast } from "@/hooks/useTransactionToast"
import { createSwapContract, WelephantSwapContract, SWAPPER_ADDRESS, WELEPHANT_ADDRESS, GAS_LIMIT } from "@/lib/contracts"
import { parseEther, BNB_DECIMALS, formatTokenAmount, formatUsdBigint } from "@/lib/ethersUtils"
import { formatUnits, parseUnits, Signer, Contract, InterfaceAbi, ContractTransactionResponse } from "ethers"
import { cn } from "@/lib/utils"
import { isPageVisible } from "@/lib/pageVisibility"
import { isUserRejection, getWalletErrorMessage, USER_REJECTED_MESSAGE, logContractError } from "@/lib/walletErrors"
import { validateBalance, isAmountExceedingBalance } from "@/lib/validateBalance"

// WELEPHANT Token ABI (minimal for approve and balanceOf)
const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
] as const

// Token decimals

const DEFAULT_WELEPHANT_DECIMALS = 9 // Matches mainnet

// Default slippage: 0.5% = 500 (out of 100000)
const DEFAULT_SLIPPAGE = 500
const SLIPPAGE_SCALE = 100000
const SLIPPAGE_STORAGE_KEY = "welephant_swap_slippage"

// Load slippage from localStorage
function loadSlippage(): number {
  if (typeof window === "undefined") return DEFAULT_SLIPPAGE
  const stored = localStorage.getItem(SLIPPAGE_STORAGE_KEY)
  if (stored) {
    const value = parseInt(stored, 10)
    if (!isNaN(value) && value >= 10 && value <= 5000) {
      return value
    }
  }
  return DEFAULT_SLIPPAGE
}

// Save slippage to localStorage
function saveSlippage(value: number): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(SLIPPAGE_STORAGE_KEY, value.toString())
  }
}

type SwapDirection = "BNB_TO_WELEPHANT" | "WELEPHANT_TO_BNB"

export function SwapPage() {
  const { provider, signer, address, isConnected } = useEthersContext()
  const { showTxPending, showTxError } = useTransactionToast()

  // Swap state
  const [direction, setDirection] = useState<SwapDirection>("BNB_TO_WELEPHANT")
  const [inputAmount, setInputAmount] = useState("")
  const [outputAmount, setOutputAmount] = useState("")
  const [slippage, setSlippage] = useState(loadSlippage)
  const [slippageDialogOpen, setSlippageDialogOpen] = useState(false)
  const [amountPercent, setAmountPercent] = useState(0)
  const isUpdatingFromSlider = useRef(false)

  // Loading states
  const [isEstimating, setIsEstimating] = useState(false)
  const [isSwapping, setIsSwapping] = useState(false)
  const [isApproving, setIsApproving] = useState(false)

  // Error state
  const [error, setError] = useState<string | null>(null)

  // Balances
  const [bnbBalance, setBnbBalance] = useState<bigint>(0n)
  const [welephantBalance, setWelephantBalance] = useState<bigint>(0n)

  // USD values
  const [inputUsd, setInputUsd] = useState<string>("$0.00")
  const [outputUsd, setOutputUsd] = useState<string>("$0.00")

  // Balance USD values (for ToggleableAmount)
  const [bnbBalanceUsd, setBnbBalanceUsd] = useState<string | null>(null)
  const [welephantBalanceUsd, setWelephantBalanceUsd] = useState<string | null>(null)

  // Allowance for WELEPHANT
  const [allowance, setAllowance] = useState<bigint>(0n)

  // WELEPHANT decimals (fetched from contract, default matches mainnet)
  const [welephantDecimals, setWelephantDecimals] = useState<number>(DEFAULT_WELEPHANT_DECIMALS)

  // Contracts
  const swapContract = useMemo(() => {
    if (!provider) return null
    return createSwapContract(provider)
  }, [provider])

  const welephantContract = useMemo(() => {
    if (!provider) return null
    return new Contract(WELEPHANT_ADDRESS, ERC20_ABI as InterfaceAbi, provider)
  }, [provider])

  // Fetch WELEPHANT decimals
  useEffect(() => {
    const fetchDecimals = async () => {
      if (!welephantContract) return
      try {
        const decimals = await welephantContract.decimals()
        setWelephantDecimals(Number(decimals))
      } catch (err) {
        console.error("Failed to fetch WELEPHANT decimals:", err)
      }
    }
    fetchDecimals()
  }, [welephantContract])

  // Get decimals for current direction
  const inputDecimals = direction === "BNB_TO_WELEPHANT" ? BNB_DECIMALS : welephantDecimals
  const outputDecimals = direction === "BNB_TO_WELEPHANT" ? welephantDecimals : BNB_DECIMALS

  // Fetch balances
  useEffect(() => {
    const fetchBalances = async () => {
      if (!isPageVisible()) return
      if (!provider || !address) return

      try {
        // BNB balance from provider
        const bnb = await provider.getBalance(address)
        setBnbBalance(bnb)

        // WELEPHANT balance
        if (welephantContract && address) {
          const wel = await welephantContract.balanceOf(address)
          setWelephantBalance(wel)
        }
      } catch (err) {
        console.error("Failed to fetch balances:", err)
      }
    }

    fetchBalances()
    const interval = setInterval(fetchBalances, 10000)
    return () => clearInterval(interval)
  }, [provider, address, welephantContract])

  // Compute USD values for wallet balances
  useEffect(() => {
    const computeBalanceUsd = async () => {
      if (!swapContract) return
      try {
        if (bnbBalance > 0n) {
          const usdValue = await swapContract.estimateUsdForETH(bnbBalance)
          setBnbBalanceUsd(formatUsdBigint(usdValue))
        } else {
          setBnbBalanceUsd(null)
        }

        if (welephantBalance > 0n) {
          const pricePerToken = await swapContract.getWelephantPriceInUSD()
          const usdValue = pricePerToken * welephantBalance / BigInt(10 ** welephantDecimals)
          setWelephantBalanceUsd(formatUsdBigint(usdValue))
        } else {
          setWelephantBalanceUsd(null)
        }
      } catch (err) {
        console.error("Failed to compute balance USD:", err)
      }
    }

    computeBalanceUsd()
  }, [swapContract, bnbBalance, welephantBalance])

  // Check allowance for WELEPHANT->BNB swaps
  useEffect(() => {
    const checkAllowance = async () => {
      if (!welephantContract || !address || direction !== "WELEPHANT_TO_BNB") return

      try {
        const currentAllowance = await welephantContract.allowance(address, SWAPPER_ADDRESS)
        setAllowance(currentAllowance)
      } catch (err) {
        console.error("Failed to check allowance:", err)
      }
    }

    checkAllowance()
  }, [welephantContract, address, direction])

  // Persist slippage to localStorage
  useEffect(() => {
    saveSlippage(slippage)
  }, [slippage])

  // Get max input balance (leaving gas for BNB)
  const maxInputBalance = direction === "BNB_TO_WELEPHANT"
    ? (bnbBalance > parseEther("0.01") ? bnbBalance - parseEther("0.01") : 0n)
    : welephantBalance

  // Update slider when input changes manually (not from slider)
  useEffect(() => {
    if (isUpdatingFromSlider.current) {
      isUpdatingFromSlider.current = false
      return
    }
    if (!inputAmount || maxInputBalance === 0n) {
      setAmountPercent(0)
      return
    }
    try {
      const inputWei = parseUnits(inputAmount, inputDecimals)
      const percent = Number((inputWei * 100n) / maxInputBalance)
      setAmountPercent(Math.min(100, Math.max(0, percent)))
    } catch {
      // Invalid input, ignore
    }
  }, [inputAmount, maxInputBalance, inputDecimals])

  // Reset amount percent when direction changes
  useEffect(() => {
    setAmountPercent(0)
  }, [direction])

  // Handle slider change
  const handleAmountSliderChange = useCallback((percent: number) => {
    setAmountPercent(percent)
    if (maxInputBalance === 0n) {
      setInputAmount("")
      return
    }
    isUpdatingFromSlider.current = true
    const amountWei = (maxInputBalance * BigInt(percent)) / 100n
    setInputAmount(formatUnits(amountWei, inputDecimals))
  }, [maxInputBalance, inputDecimals])

  // Estimate output amount
  const estimateOutput = useCallback(async () => {
    if (!swapContract || !inputAmount || parseFloat(inputAmount) <= 0) {
      setOutputAmount("")
      setInputUsd("$0.00")
      setOutputUsd("$0.00")
      return
    }

    setIsEstimating(true)
    setError(null)

    try {
      const inputWei = parseUnits(inputAmount, inputDecimals)

      if (direction === "BNB_TO_WELEPHANT") {
        // Get estimated WELEPHANT output (estimateMinWrappedForETH handles wrap + fees)
        const estimateAmount = await swapContract.estimateMinWrappedForETH(inputWei, 0n)
        setOutputAmount(formatTokenAmount(estimateAmount, outputDecimals))

        // Get USD value of input BNB
        const usdValue = await swapContract.estimateUsdForETH(inputWei)
        setInputUsd(`$${formatUsdBigint(usdValue)}`)
        setOutputUsd(`$${formatUsdBigint(usdValue)}`) // Approximate same value
      } else {
        // Get estimated BNB output (estimateMinETHForWrapped handles unwrap + fees)
        const estimateAmount = await swapContract.estimateMinETHForWrapped(inputWei, 0n)
        setOutputAmount(formatTokenAmount(estimateAmount, outputDecimals))

        // Get USD value of output BNB
        const usdValue = await swapContract.estimateUsdForETH(estimateAmount)
        setInputUsd(`$${formatUsdBigint(usdValue)}`)
        setOutputUsd(`$${formatUsdBigint(usdValue)}`)
      }
    } catch (err) {
      console.error("Failed to estimate:", err)
      setError("Failed to get price estimate")
    } finally {
      setIsEstimating(false)
    }
  }, [swapContract, inputAmount, direction, inputDecimals, outputDecimals])

  // Debounced estimation
  useEffect(() => {
    const timer = setTimeout(estimateOutput, 300)
    return () => clearTimeout(timer)
  }, [estimateOutput])

  // Toggle swap direction
  const toggleDirection = () => {
    setDirection(d => d === "BNB_TO_WELEPHANT" ? "WELEPHANT_TO_BNB" : "BNB_TO_WELEPHANT")
    setInputAmount("")
    setOutputAmount("")
    setError(null)
  }

  // Set max input
  const setMaxInput = () => {
    if (direction === "BNB_TO_WELEPHANT") {
      // Leave some BNB for gas
      const maxBnb = bnbBalance > parseEther("0.01") ? bnbBalance - parseEther("0.01") : 0n
      setInputAmount(formatUnits(maxBnb, BNB_DECIMALS))
    } else {
      setInputAmount(formatUnits(welephantBalance, welephantDecimals))
    }
  }

  // Approve WELEPHANT spending
  const handleApprove = async () => {
    if (!signer || !welephantContract) return

    // Pre-flight gas check
    const validation = validateBalance({ balance: bnbBalance })
    if (!validation.valid) {
      showTxError("Approve", validation.error!)
      return
    }

    setIsApproving(true)
    setError(null)

    try {
      const inputWei = parseUnits(inputAmount, welephantDecimals)
      const connectedContract = welephantContract.connect(signer as Signer) as Contract & {
        approve(spender: string, amount: bigint): Promise<ContractTransactionResponse>
      }
      const tx = await connectedContract.approve(SWAPPER_ADDRESS, inputWei, { gasLimit: GAS_LIMIT })
      const txToast = showTxPending("Approve", tx.hash)
      await tx.wait()
      txToast.success()
      setAllowance(inputWei)
    } catch (err: unknown) {
      logContractError("Swap.approve", err)
      if (isUserRejection(err)) {
        showTxError("Approve", USER_REJECTED_MESSAGE)
      } else {
        const errorMessage = getWalletErrorMessage(err, "Approval failed")
        showTxError("Approve", errorMessage || "Approval failed")
        setError(errorMessage)
      }
    } finally {
      setIsApproving(false)
    }
  }

  // Execute swap
  const handleSwap = async () => {
    if (!swapContract || !signer || !address || !inputAmount) return

    // Pre-flight balance check
    const inputWei = parseUnits(inputAmount, inputDecimals)
    if (direction === "BNB_TO_WELEPHANT") {
      const validation = validateBalance({ balance: bnbBalance, value: inputWei })
      if (!validation.valid) {
        showTxError("Swap", validation.error!)
        return
      }
    } else {
      const validation = validateBalance({
        balance: bnbBalance,
        tokenBalance: welephantBalance,
        tokenAmount: inputWei,
        tokenSymbol: "WELEPHANT",
        tokenDecimals: welephantDecimals,
      })
      if (!validation.valid) {
        showTxError("Swap", validation.error!)
        return
      }
    }

    setIsSwapping(true)
    setError(null)

    try {
      const connectedContract = swapContract.connect(signer as Signer) as WelephantSwapContract

      if (direction === "BNB_TO_WELEPHANT") {
        // Calculate min output with slippage
        const minOut = await swapContract.estimateMinWrappedForETH(inputWei, BigInt(slippage))

        const tx = await connectedContract.swapExactETHForWrapped(
          minOut,
          address,
          { value: inputWei, gasLimit: GAS_LIMIT }
        )
        const txToast = showTxPending("Swap", tx.hash)
        await tx.wait()
        txToast.success()
      } else {
        // Calculate min output with slippage
        const minOut = await swapContract.estimateMinETHForWrapped(inputWei, BigInt(slippage))

        const tx = await connectedContract.swapExactWrappedForETH(
          inputWei,
          minOut,
          address,
          { gasLimit: GAS_LIMIT }
        )
        const txToast = showTxPending("Swap", tx.hash)
        await tx.wait()
        txToast.success()
      }

      // Clear form
      setInputAmount("")
      setOutputAmount("")
    } catch (err: unknown) {
      logContractError("Swap.swap", err)
      if (isUserRejection(err)) {
        showTxError("Swap", USER_REJECTED_MESSAGE)
      } else {
        const errorMessage = getWalletErrorMessage(err, "Swap failed")
        showTxError("Swap", errorMessage || "Swap failed")
        setError(errorMessage)
      }
    } finally {
      setIsSwapping(false)
    }
  }

  // Check if approval is needed
  const needsApprovalCheck = (): boolean => {
    if (direction !== "WELEPHANT_TO_BNB" || !inputAmount || parseFloat(inputAmount) <= 0) {
      return false
    }
    try {
      return parseUnits(inputAmount, welephantDecimals) > allowance
    } catch {
      return false
    }
  }
  const needsApproval = needsApprovalCheck()

  // Check if swap is possible
  const canSwap = isConnected &&
    inputAmount &&
    parseFloat(inputAmount) > 0 &&
    !isEstimating &&
    !isSwapping &&
    !needsApproval

  // Get current input/output token info
  const inputToken = direction === "BNB_TO_WELEPHANT"
    ? { symbol: "BNB", balance: bnbBalance, decimals: BNB_DECIMALS, balanceUsd: bnbBalanceUsd }
    : { symbol: "WELEPHANT", balance: welephantBalance, decimals: welephantDecimals, balanceUsd: welephantBalanceUsd }

  const outputToken = direction === "BNB_TO_WELEPHANT"
    ? { symbol: "WELEPHANT", balance: welephantBalance, decimals: welephantDecimals, balanceUsd: welephantBalanceUsd }
    : { symbol: "BNB", balance: bnbBalance, decimals: BNB_DECIMALS, balanceUsd: bnbBalanceUsd }

  // Calculate slippage percentage for display
  const slippagePercent = (slippage / SLIPPAGE_SCALE * 100).toFixed(1)

  return (
    <>
      <SEO
        title="Swap"
        url="/swap"
        description="Swap BNB for WELEPHANT tokens instantly on BNB Chain. Low fees, fast execution, and transparent pricing."
        keywords="token swap, BNB to WELEPHANT, DEX, decentralized exchange, crypto swap"
      />
      <div className="container py-16">
      <div className="mx-auto max-w-md">
        <Card className="overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Swap</h2>
            <SlippageDialog
              slippage={slippage}
              onSlippageChange={setSlippage}
              open={slippageDialogOpen}
              onOpenChange={setSlippageDialogOpen}
            />
          </div>

          <CardContent className="p-4 space-y-2">
            {/* From Token */}
            <div className={cn("rounded-xl bg-muted/50 p-4", isAmountExceedingBalance(inputAmount, inputToken.balance, inputToken.decimals) && "bg-destructive/10 border border-destructive/30")}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-muted-foreground">From</span>
                <TokenButton symbol={inputToken.symbol} />
              </div>

              <div className="flex items-center justify-end gap-2 mb-3">
                <Slider
                  value={[amountPercent]}
                  onValueChange={([value]) => handleAmountSliderChange(value)}
                  min={0}
                  max={100}
                  step={1}
                  className="w-24"
                />
                <span className="text-sm text-primary w-10 text-right">{amountPercent}%</span>
              </div>

              <div className="mb-2">
                <Input
                  type="number"
                  placeholder="0"
                  value={inputAmount}
                  onChange={(e) => setInputAmount(e.target.value)}
                  className="text-2xl !border-0 bg-transparent p-0 h-auto text-right"
                />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={setMaxInput}
                  className={cn("flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors", isAmountExceedingBalance(inputAmount, inputToken.balance, inputToken.decimals) && "text-destructive")}
                >
                  <Wallet className="h-3 w-3" />
                  <span onClick={(e) => e.stopPropagation()}>
                    <ToggleableAmount
                      amount={formatTokenAmount(inputToken.balance, inputToken.decimals)}
                      symbol={inputToken.symbol}
                      usdValue={inputToken.balanceUsd}

                    />
                  </span>
                </button>
                <span className="text-sm text-muted-foreground">{inputUsd}</span>
              </div>
            </div>

            {/* Swap Direction Button */}
            <div className="flex justify-center -my-1 relative z-10">
              <button
                onClick={toggleDirection}
                className="rounded-full border-4 border-background bg-muted p-2 hover:bg-muted/80 transition-colors"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
            </div>

            {/* To Token */}
            <div className="rounded-xl bg-muted/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-muted-foreground">To</span>
                <TokenButton symbol={outputToken.symbol} />
              </div>

              <div className={cn(
                "text-2xl font-medium mb-2 text-right",
                isEstimating && "animate-pulse"
              )}>
                {isEstimating ? (
                  <Loader2 className="h-6 w-6 animate-spin ml-auto" />
                ) : (
                  outputAmount || "0"
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Wallet className="h-3 w-3" />
                  <ToggleableAmount
                    amount={formatTokenAmount(outputToken.balance, outputToken.decimals)}
                    symbol={outputToken.symbol}
                    usdValue={outputToken.balanceUsd}
                  />
                </span>
                <span className="text-sm text-muted-foreground">{outputUsd}</span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm p-3 rounded-lg bg-destructive/10">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Action Button */}
            {!isConnected ? (
              <Button disabled className="w-full h-14 text-lg">
                Connect Wallet
              </Button>
            ) : needsApproval ? (
              <Button
                onClick={handleApprove}
                disabled={isApproving}
                className="w-full h-14 text-lg"
              >
                {isApproving ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Approving...
                  </>
                ) : (
                  `Approve ${inputToken.symbol}`
                )}
              </Button>
            ) : (
              <Button
                onClick={handleSwap}
                disabled={!canSwap}
                className="w-full h-14 text-lg"
              >
                {isSwapping ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Swapping...
                  </>
                ) : (
                  "Swap"
                )}
              </Button>
            )}

            {/* Swap Details */}
            {inputAmount && outputAmount && (
              <div className="text-xs text-muted-foreground space-y-1 pt-2">
                <div className="flex justify-between">
                  <span>Rate</span>
                  <span>
                    1 {inputToken.symbol} ≈ {(parseFloat(outputAmount) / parseFloat(inputAmount)).toFixed(6)} {outputToken.symbol}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Slippage Tolerance</span>
                  <span>{slippagePercent}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Minimum Received</span>
                  <span>
                    {(parseFloat(outputAmount) * (SLIPPAGE_SCALE - slippage) / SLIPPAGE_SCALE).toLocaleString(undefined, { maximumFractionDigits: 6 })} {outputToken.symbol}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  )
}

// Token button component
function TokenButton({ symbol }: { symbol: string }) {
  const logo = symbol === "BNB" ? bnbLogo : welephantLogo

  return (
    <div className="flex items-center gap-2">
      <img src={logo} alt={symbol} className="w-6 h-6" />
      <span className="font-medium">{symbol}</span>
    </div>
  )
}

// Slippage settings dialog
function SlippageDialog({
  slippage,
  onSlippageChange,
  open,
  onOpenChange,
}: {
  slippage: number
  onSlippageChange: (value: number) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const presets = [100, 500, 1000] // 0.1%, 0.5%, 1%

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button className="rounded-lg p-2 hover:bg-muted transition-colors">
          <Settings className="h-5 w-5" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Swap Settings</DialogTitle>
          <DialogDescription>
            Configure your swap preferences
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Slippage Tolerance
            </label>
            <p className="text-xs text-muted-foreground mb-3">
              Your transaction will revert if the price changes unfavorably by more than this percentage.
            </p>

            {/* Preset buttons */}
            <div className="flex gap-2 mb-4">
              {presets.map((preset) => (
                <Button
                  key={preset}
                  variant={slippage === preset ? "default" : "outline"}
                  size="sm"
                  onClick={() => onSlippageChange(preset)}
                >
                  {(preset / SLIPPAGE_SCALE * 100).toFixed(1)}%
                </Button>
              ))}
            </div>

            {/* Slider */}
            <div className="space-y-2">
              <Slider
                value={[slippage]}
                onValueChange={([value]) => onSlippageChange(value)}
                min={10}
                max={5000}
                step={10}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0.01%</span>
                <span className="font-medium text-foreground">
                  {(slippage / SLIPPAGE_SCALE * 100).toFixed(2)}%
                </span>
                <span>5%</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Helper functions
